import { Component, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { JsignalService } from 'src/app/shared/services/jsignal.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocalStorageSettingsService } from 'src/app/shared/services/local-storage-settings.service';

@Component({
    selector: 'app-action-dialog',
    templateUrl: './action-dialog.component.html',
    styleUrls: ['./action-dialog.component.scss'],
    standalone: false
})
export class ActionDialogComponent implements OnInit {

  form!: FormGroup;

  titleText = 'Create New Action';
  isEditMode = false;
  showOtherInput = false;
  externalId = '';
  externalType = '';
  formSubmitted = false; // ADDED: Track form submission

  actionNameList: any[] = [];
  actionOriginList: any[] = [];
  actionPriorityList: any[] = [];

  priorityDueDateMap: Record<string, number> = {
    Standard: 90,
    Emergency: 60,
    Urgent: 30
  };

  lookups = [
    { key: 'ActionNames', assignTo: 'actionNameList' },
    { key: 'ActionOrigins', assignTo: 'actionOriginList' },
    { key: 'ActionPriorities', assignTo: 'actionPriorityList' }
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private jsignalService: JsignalService,
    private commonService: CommonService,
    private spinner: NgxUiLoaderService,
    private snackBar: MatSnackBar,
    private localStorage: LocalStorageSettingsService,
    
  ) {

    if (data?.externalId) {
      this.externalId = data.externalId;
      this.externalType = data.externalType;
    }

    if (data?.action) {
      this.isEditMode = true;
      this.titleText = 'Update Action';
    }

  }

  // =========================
  // INIT
  // =========================

  ngOnInit(): void {

    this.initForm();
    this.loadDropdowns();

  }

  // =========================
  // FORM INIT
  // =========================

  initForm(): void {

    this.form = this.fb.group({

      id: [null],

      actionId: [null],

      actionName: ['', Validators.required],

      actionOther: [''],

      actionOrigin: ['', Validators.required],

      actionPriority: ['', Validators.required],

      actionDescription: ['', Validators.required],

      dueDate: ['', Validators.required],

      externalId: [this.externalId],
      externalType: [this.externalType] 

    });

    this.handleActionNameChanges();
    this.handlePriorityChanges();
    
    this.formSubmitted = false;
    
  }

  // =========================
  // HANDLE ACTION NAME CHANGE
  // =========================

  handleActionNameChanges(): void {

    this.form.get('actionName')?.valueChanges.subscribe(value => {

      this.showOtherInput = value === 'Other';

      const otherControl = this.form.get('actionOther');

      if (this.showOtherInput) {
        otherControl?.setValidators([Validators.required]);
      }
      else {
        otherControl?.clearValidators();
        otherControl?.setValue('');
      }

      otherControl?.updateValueAndValidity();

    });

  }

  // =========================
  // HANDLE PRIORITY CHANGE
  // =========================

  handlePriorityChanges(): void {

    this.form.get('actionPriority')?.valueChanges.subscribe(priority => {

      if (priority && this.priorityDueDateMap[priority]) {
        this.setDueDate(priority);
      }

    });

  }

  // =========================
  // LOAD LOOKUPS
  // =========================

  loadDropdowns(): void {

    this.spinner.start();

    let completed = 0;

    this.lookups.forEach(lookup => {

      this.commonService
        .getlookupVauleByLookUpType(lookup.key)
        .subscribe({

          next: (res: any) => {

            (this as any)[lookup.assignTo] =
              Array.isArray(res) ? res : [];

            this.checkDropdownCompletion(++completed);

          },

          error: () => {

            (this as any)[lookup.assignTo] = [];

            this.checkDropdownCompletion(++completed);

          }

        });

    });

  }

  checkDropdownCompletion(completed: number): void {

    if (completed === this.lookups.length) {

      this.spinner.stop();

      if (this.isEditMode) {
        this.populateForm();
      }

    }

  }

  // =========================
  // POPULATE FORM (EDIT)
  // =========================

  populateForm(): void {

    const a = this.data.action;

    this.form.patchValue({

      id: a.id,

      actionId: a.actionId,

      actionName: a.actionName,

      actionOrigin: a.originatingFrom,

      actionPriority: a.priority,

      actionDescription: a.actionDescription,

      dueDate: a.dueDate,

      externalId: a.externalId,
      externalType: a.externalType

    });

    if (a.actionName === 'Other' && a.actionOther) {
      this.showOtherInput = true;
      this.form.patchValue({ actionOther: a.actionOther });
      
      // Set validator for actionOther
      const otherControl = this.form.get('actionOther');
      otherControl?.setValidators([Validators.required]);
      otherControl?.updateValueAndValidity();
    }
    

  }

  // =========================
  // SET DUE DATE
  // =========================

  setDueDate(priority: string): void {

    const days = this.priorityDueDateMap[priority];

    if (!days) return;

    const dueDate = new Date();

    dueDate.setDate(dueDate.getDate() + days);

    const year = dueDate.getFullYear();
    const month = String(dueDate.getMonth() + 1).padStart(2, '0');
    const day = String(dueDate.getDate()).padStart(2, '0');
    
    this.form.patchValue({
      dueDate: `${year}-${month}-${day}`
    });

  }

  // =========================
  // VALIDATION HELPER (FIXED)
  // =========================

  isInvalid(controlName: string): boolean {

    const control = this.form.get(controlName);
    
    return !!(control && 
              control.invalid && 
              (control.touched || this.formSubmitted));

  }

  // =========================
  // SAVE (FIXED)
  // =========================

  save(): void {

    this.formSubmitted = true;

    if (this.form.invalid) {

      this.form.markAllAsTouched();
      
      this.snackBar.open(
        'Please fill in all required fields',
        'OK',
        { duration: 3000 }
      );
      
      return;

    }

    this.spinner.start();

    const f = this.form.value;

    const finalActionName =
      f.actionName === 'Other'
        ? f.actionOther
        : f.actionName;
        
    const DnsName = this.localStorage.getSettings("dnsName");
    const Workspaceid = this.localStorage.getSettings("WorkspaceId");
    
    const dto = {

      actionName: finalActionName,

      originatingFrom: f.actionOrigin,

      priority: f.actionPriority,

      dueDate: f.dueDate,

      actionDescription: f.actionDescription,

      externalId: f.externalId,
      externalType: f.externalType,
      dnsId: DnsName,
      workspaceId: Workspaceid,

    };

    if (this.isEditMode) {

      this.updateAction(f.id, dto);

    }
    else {

      this.createAction(dto);

    }

  }

  // =========================
  // CREATE
  // =========================

  createAction(dto: any): void {

    this.jsignalService.createAction(dto)
      .subscribe({

        next: () => {

          this.spinner.stop();

          this.snackBar.open(
            'Action created successfully',
            'OK',
            { duration: 3000 }
          );

          this.dialogRef.close(true);

        },

        error: err => {

          this.spinner.stop();

          this.snackBar.open(
            err.error?.message || 'Create failed',
            'OK',
            { duration: 3000 }
          );

        }

      });

  }

  // =========================
  // UPDATE
  // =========================

  updateAction(id: number, dto: any): void {

    this.jsignalService.updateAction(id, dto)
      .subscribe({

        next: () => {

          this.spinner.stop();

          this.snackBar.open(
            'Action updated successfully',
            'OK',
            { duration: 3000 }
          );

          this.dialogRef.close(true);

        },

        error: err => {

          this.spinner.stop();

          this.snackBar.open(
            err.error?.message || 'Update failed',
            'OK',
            { duration: 3000 }
          );

        }

      });

  }

  // =========================
  // CLOSE
  // =========================

  close(): void {

    this.dialogRef.close(false);

  }

}